import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';

export type Category = 'proof_docs' | 'qualifying_programs';

export interface FetchLmiProofDocLabelOptionsParams {
  utilityId: number;
  category: Category;
}

export interface FetchLmiProofDocLabelOptionsResponse {
  data: Record<string, string | string[]> | null;
}

export const LMI_PROOF_UPLOAD_CATEGORY = 'proof_docs' as const;

export type NormalizedLmiProofDocLabelOptions = string[] | Record<string, string> | null;

export function isValidFetchLmiProofDocLabelOptionsParams(
  params: Partial<FetchLmiProofDocLabelOptionsParams>,
): params is FetchLmiProofDocLabelOptionsParams {
  return (
    typeof params.utilityId === 'number' &&
    params.utilityId > 0 &&
    (params.category === 'proof_docs' || params.category === 'qualifying_programs')
  );
}

/**
 * Maps GraphQL `lmiProofDocLabelOptions.data` into either a label array or key→label record.
 */
export function normalizeLmiProofDocLabelData(
  data: Record<string, string | string[]> | null,
): NormalizedLmiProofDocLabelOptions {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const values = Object.values(data);

  if (values.length === 1 && Array.isArray(values[0])) {
    return values[0].filter((v): v is string => typeof v === 'string');
  }

  const record: Record<string, string> = {};
  for (const [key, val] of Object.entries(data)) {
    if (typeof val === 'string') {
      record[key] = val;
    }
  }
  if (Object.keys(record).length === 0) {
    return null;
  }
  return record;
}

export function getFetchLmiProofDocLabelOptionsParams(
  funnelPacket: FetchFunnelPacketResponse | undefined,
  category: Category = LMI_PROOF_UPLOAD_CATEGORY,
): FetchLmiProofDocLabelOptionsParams | null {
  const utilityId = funnelPacket?.utility?.id;
  if (!utilityId) {
    return null;
  }

  const params = { utilityId, category };
  return isValidFetchLmiProofDocLabelOptionsParams(params) ? params : null;
}
